<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';

const STATE_STORAGE_KEY = 'slackOauthState';

const BOT_SCOPES = [
  'chat:write',
  'chat:write.customize',
  'channels:history',
  'groups:history',
  'channels:read',
  'groups:read',
  'users:read',
  'users:read.email',
  'reactions:write',
];

export default {
  name: 'SlackSetup',
  components: {
    LoadingState,
    PageHeader,
  },
  setup() {
    const { accountId } = useAccount();
    return {
      accountId,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      inboxName: '',
      authCode: null,
      authState: null,
      stateMismatch: false,
    };
  },
  validations: {
    inboxName: { required },
  },
  computed: {
    hasAuthCode() {
      return !!this.authCode;
    },
    slackClientId() {
      return window.chatwootConfig?.slackClientId || '';
    },
    redirectUri() {
      return window.chatwootConfig?.slackRedirectUri || '';
    },
  },
  mounted() {
    this.checkForAuthCode();
  },
  methods: {
    checkForAuthCode() {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      if (!code) return;

      const returnedState = urlParams.get('state') || '';
      const issuedState = this.readIssuedState();
      window.history.replaceState({}, document.title, window.location.pathname);

      if (!issuedState || returnedState !== issuedState) {
        this.stateMismatch = true;
        return;
      }

      this.authCode = code;
      this.authState = returnedState;
    },
    randomNonce() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();
      if (!window.crypto?.getRandomValues) return null;

      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(
        ''
      );
    },
    issueState() {
      const nonce = this.randomNonce();
      if (!nonce) return null;

      const state = `${this.accountId}:${nonce}`;
      try {
        window.sessionStorage.setItem(STATE_STORAGE_KEY, state);
      } catch (error) {
        return null;
      }
      return state;
    },
    readIssuedState() {
      try {
        const state = window.sessionStorage.getItem(STATE_STORAGE_KEY);
        window.sessionStorage.removeItem(STATE_STORAGE_KEY);
        return state;
      } catch (error) {
        return null;
      }
    },
    buildAuthUrl(state) {
      const params = new URLSearchParams({
        client_id: this.slackClientId,
        scope: BOT_SCOPES.join(','),
        redirect_uri: this.redirectUri,
        state,
      });
      return `https://slack.com/oauth/v2/authorize?${params.toString()}`;
    },
    startLogin() {
      if (!this.redirectUri || !this.slackClientId) {
        useAlert(this.$t('INBOX_MGMT.ADD.SLACK_CHANNEL.CONFIG_MISSING'));
        return;
      }

      const state = this.issueState();
      if (!state) {
        useAlert(this.$t('INBOX_MGMT.ADD.SLACK_CHANNEL.STATE_UNAVAILABLE'));
        return;
      }
      window.location.href = this.buildAuthUrl(state);
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.isCreating = true;
      try {
        const data = await this.$store.dispatch('inboxes/createSlackChannel', {
          code: this.authCode,
          state: this.authState,
          inbox_name: this.inboxName,
        });

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.id },
        });
      } catch (error) {
        this.authCode = null;
        this.authState = null;
        this.isCreating = false;
        useAlert(
          error?.response?.data?.error ||
            this.$t('INBOX_MGMT.ADD.SLACK_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 min-h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0 self-start"
  >
    <div
      v-if="stateMismatch"
      class="max-w-lg mx-auto pt-6 text-sm text-red-700 dark:text-red-300"
    >
      {{ $t('INBOX_MGMT.ADD.SLACK_CHANNEL.STATE_MISMATCH') }}
    </div>

    <div
      v-else-if="!hasAuthCode && !isCreating"
      class="flex flex-col items-center max-w-lg mx-auto pt-6"
    >
      <img
        src="/assets/images/dashboard/channels/slack.png"
        alt="Slack"
        class="w-16 h-16 mb-5"
      />

      <PageHeader
        class="text-center items-center"
        :header-title="$t('INBOX_MGMT.ADD.SLACK_CHANNEL.TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.SLACK_CHANNEL.DESC')"
      />

      <woot-button class="mt-6" icon="add" @click="startLogin">
        {{ $t('INBOX_MGMT.ADD.SLACK_CHANNEL.CONNECT_BUTTON') }}
      </woot-button>
    </div>

    <LoadingState
      v-else-if="isCreating"
      :message="$t('INBOX_MGMT.ADD.SLACK_CHANNEL.CREATING')"
    />

    <div v-else class="max-w-lg mx-auto pt-6 pb-6">
      <div
        class="flex items-center gap-3 p-3 mb-6 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/40"
      >
        <fluent-icon
          icon="checkmark"
          size="16"
          class="text-green-600 dark:text-green-400"
        />
        <span class="text-sm font-medium text-green-700 dark:text-green-300">
          {{ $t('INBOX_MGMT.ADD.SLACK_CHANNEL.CONNECTED_SUCCESS') }}
        </span>
      </div>

      <PageHeader
        :header-title="$t('INBOX_MGMT.ADD.SLACK_CHANNEL.SETUP_TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.SLACK_CHANNEL.SETUP_SUBTITLE')"
      />

      <form class="mt-6" @submit.prevent="createChannel">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.SLACK_CHANNEL.INBOX_NAME') }}
          <input
            v-model.trim="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.SLACK_CHANNEL.INBOX_NAME_PLACEHOLDER')
            "
            @input="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.SLACK_CHANNEL.INBOX_NAME_REQUIRED') }}
          </span>
        </label>

        <div class="mt-4">
          <woot-submit-button
            :loading="isCreating"
            :button-text="$t('INBOX_MGMT.ADD.SLACK_CHANNEL.SUBMIT_BUTTON')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
